import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useState, useEffect, useRef } from 'react';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { FiEye } from 'react-icons/fi';
import { Pagination } from '~/components/Pagination';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchchangerequest, fetchChangeRequestStatusCounts } from '../../../services/change-request';
import { defaultFundRequestFilters } from '~/services/fund-requests';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import FundRequestStatusTabs from '../FundRequestStatusTabs';
import {
    ChangeRequestFilters,
    VendorChangeRequest,
} from './RequestChanges.types';

const statusTabs = [
    { label: 'All', status: '' },
    { label: 'Needs Review', status: 'pending' },
    { label: 'Approved', status: 'approved' },
    { label: 'Rejected', status: 'rejected' },
];

const sortByMap: Record<string, string> = {
    vendor: 'vendor_name',
};

enum Sort {
    ASC = 'asc',
    DESC = 'desc',
}

const generatePageName = (status: string) => {
    switch (status) {
        case 'pending':
            return 'Needs Review Vendor Change Requests';
        case 'approved':
            return 'Approved Vendor Change Requests';
        case 'rejected':
            return 'Rejected Vendor Change Requests';
        default:
            return 'Vendor Change Requests';
    }
};

const RequestChanges: React.FC = () => {
    const [pages, setPages] = useState<number>(1);
    const [page, setPage] = useState<number>(1);
    const [filters, setFilters] = useState<ChangeRequestFilters>(
        defaultFundRequestFilters
    );
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [status, setStatus] = useState<string>('');
    const [statusCounts, setStatusCounts] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0,
    });

    const columnHelper = createColumnHelper<
        VendorChangeRequest & { action?: string }
    >();

    const { data, isLoading } = useQuery({
        queryKey: ['changeRequest', filters, status],
        queryFn: async () => {
            const data = await fetchchangerequest(
                page,
                filters.sort,
                filters.sort_by,
                status || undefined
            );
            return data;
        },
    });

    const { data: countData } = useQuery({
        queryKey: ['changeRequestCounts'],
        queryFn: async () => {
            return await fetchChangeRequestStatusCounts();
        },
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (countData) {
            setStatusCounts(countData);
        }
    }, [countData]);

    useEffect(() => {
        if (data?.results?.length) {
            const total = data?.count;
            if (total) {
                setPages(Math.ceil(total / 20));
            }
        }
    }, [data?.results]);

    useEffect(() => {
        const urlStatus = searchParams.get('status');
        setStatus(urlStatus || '');
        if (urlStatus) {
            const newFilters = { ...filters, status: urlStatus };
            setFilters(newFilters);
        } else {
            setFilters({ ...filters });
        }
    }, [searchParams]);

    const columns = [
        columnHelper.accessor('vendor', {
            header: () => 'Vendor',
            cell: (props) => {
                const row = props.row.original;
                return (
                    <div>
                        <div
                            className='font-semibold'
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                                navigate(`/payees/request-changes/${row.id}`);
                            }}
                        >
                            {row?.vendor?.name}
                        </div>
                        <div
                            className='text-secondary'
                            style={{ fontSize: 'small' }}
                        >
                            {row?.vendor?.vendor_id}
                        </div>
                    </div>
                );
            },
        }),
        columnHelper.accessor('fund_name', {
            header: () => 'Fund Name',
            cell: (info) => info.renderValue(),
        }),
        columnHelper.accessor('financial_coordinator', {
            header: () => 'Coordinator',
            cell: (props) => {
                const fc = props?.row?.original;
                const firstName = fc?.financial_coordinator?.first_name || '';
                const lastName = fc?.financial_coordinator?.last_name || '';

                const fullName = `${firstName} ${lastName}`.trim();

                return fullName !== '' ? fullName : '';
            },
        }),
        columnHelper.accessor('approved_date', {
            header: () => 'Status',
            cell: ({ row }) => {
                const { created_date, approved_date, rejected_date } =
                    row.original;

                const statusMap = [
                    {
                        date: created_date,
                        value: 'pending',
                        label: 'Needs Review',
                    },
                    {
                        date: approved_date,
                        value: 'approved',
                        label: 'Approved',
                    },
                    {
                        date: rejected_date,
                        value: 'rejected',
                        label: 'Rejected',
                    },
                ]
                    .filter(({ date }) => !!date)
                    .map((item) => ({
                        ...item,
                        dateObj: new Date(item.date!),
                    }));

                const mostRecent = statusMap.reduce((latest, current) =>
                    current.dateObj > latest.dateObj ? current : latest
                );

                const { value, label } = mostRecent;

                const statusClass =
                    {
                        pending: 'bg-[#952E46] text-white',
                        approved: 'bg-[#AFC12D]',
                        rejected: 'bg-danger text-white',
                    }[value] ?? 'bg-[#97D7E1]';

                return (
                    <div
                        className={`inline-flex rounded-full px-3 py-1 text-[12px]/[14.4px] hover:bg-opacity-90 ${statusClass}`}
                    >
                        {label}
                    </div>
                );
            },
        }),
        columnHelper.accessor('created_date', {
            header: () => 'Created On',
            cell: ({ row }) => {
                const { created_date } = row.original;

                return created_date
                    ? moment(created_date).format('MM/DD/YYYY')
                    : '';
            },
        }),
        columnHelper.accessor('action', {
            header: () => 'Actions',
            cell: (props) => {
                const { id } = props.row.original;
                return (
                    <div className='flex items-center gap-3'>
                        <button
                            onClick={() => {
                                navigate(`/payees/request-changes/${id}`);
                            }}
                        >
                            <FiEye className='text-primary' />
                        </button>
                    </div>
                );
            },
        }),
    ];

    const table = useReactTable<VendorChangeRequest>({
        data: data?.results,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleSort = (columnId: string) => {
        const mappedValue = sortByMap[columnId] || columnId;
        if (filters.sort_by === mappedValue) {
            handleFilterChange(
                'sort',
                filters.sort === Sort.ASC ? Sort.DESC : Sort.ASC
            );
        } else {
            handleFilterChange('sort_by', mappedValue);
            handleFilterChange('sort', Sort.ASC);
        }
    };

    const handleFilterChange = (
        key: keyof typeof filters,
        value: string | number
    ) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value,
            ...(key !== 'page' ? { page: 1 } : {}),
        }));
    };

    return (
        <>
            <Breadcrumb pageName={generatePageName(status)} />
            <FundRequestStatusTabs
                setStatus={(newStatus: string) => {
                    if (newStatus === '') {
                        setSearchParams({});
                    } else {
                        setSearchParams({ status: newStatus });
                    }
                }}
                status={status}
                statusTabs={statusTabs}
                statusCounts={{
                    '': statusCounts.total,
                    pending: statusCounts.pending,
                    approved: statusCounts.approved,
                    rejected: statusCounts.rejected
                }}
            />
            <section className='data-table-common  rounded-sm border border-stroke bg-white pb-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div
                    className='flex items-center justify-between px-8 py-1'
                    style={{ fontSize: '12px' }}
                >
                    <p>
                        Showing {Math.min(20, (data?.count || 0) - (page - 1) * 20)} of {data?.count || 0} results
                    </p>

                    <div className='flex items-center gap-5'>
                        <div className='inline-flex items-center justify-center gap-1.5'>
                            <FiEye className='text-primary' size={18} />
                            View
                        </div>
                    </div>
                </div>

                <div className='overflow-x-auto'>
                    <table className='datatable-table datatable-one w-full table-auto border-collapse break-words px-4 md:px-8 2xl:table-fixed'>
                        <thead className='border-separate px-4'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    className='border-t border-stroke bg-whiten'
                                    style={{ fontSize: '14px' }}
                                    key={headerGroup.id}
                                >
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            style={{
                                                paddingTop: '16px',
                                                paddingBottom: '16px',
                                            }}
                                            key={header.id}
                                        >
                                            <div className='flex items-center'>
                                                <span
                                                    onClick={() =>
                                                        handleSort(
                                                            header.column.id
                                                        )
                                                    }
                                                    className='cursor-pointer'
                                                >
                                                    {header.isPlaceholder
                                                        ? null
                                                        : flexRender(
                                                            header.column
                                                                .columnDef
                                                                .header,
                                                            header.getContext()
                                                        )}
                                                </span>
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {isLoading
                                ? Array.from({ length: 10 }).map((_, index) => (
                                    <tr key={index}>
                                        {columns.map((_column, colIndex) => (
                                            <td
                                                key={colIndex}
                                                className='border-b border-stroke px-4 py-2 dark:border-strokedark'
                                            >
                                                <div className='max-w-sm animate-pulse'>
                                                    <div className='h-2 max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                                </div>
                                            </td>
                                        ))}
                                        <td className='border-b border-stroke px-4 py-2 dark:border-strokedark'>
                                            <div className='float-right w-full max-w-[10px] animate-pulse'>
                                                <div className='h-[10px] max-w-[360px] rounded-full bg-gray-4 dark:bg-gray-5'></div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                                : table.getRowModel().rows.map((row) => {
                                    return (
                                        <tr key={row.id}>
                                            {row
                                                .getVisibleCells()
                                                .map((cell) => (
                                                    <td
                                                        style={{
                                                            paddingTop:
                                                                '16px',
                                                            paddingBottom:
                                                                '16px',
                                                        }}
                                                        key={cell.id}
                                                    >
                                                        {flexRender(
                                                            cell.column
                                                                .columnDef
                                                                .cell,
                                                            cell.getContext()
                                                        )}
                                                    </td>
                                                ))}
                                        </tr>
                                    );
                                })}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    page={page}
                    pages={pages}
                    setPage={(val: number) => {
                        setPage(val);
                        setFilters({ ...filters, page: val });
                    }}
                    totalCount={data?.count}
                />
            </section>
        </>
    );
};

export default RequestChanges;
